import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal.jsx';
import { Copyable } from '../ui/Copyable.jsx';
import { rentalsApi } from '../../api/rentals.js';

// Modal: "Generează contract + trimite SMS șoferului".
// Admin-ul nu semnează aici; semnătura lui e setată o dată în Contul meu
// și se aplică automat pe toate contractele. Clientul semnează pe telefonul lui.
export function SignContractModal({ open, onClose, rental, car, onSigned }) {
  const [cnp, setCnp] = useState('');
  const [ciSerieNr, setCiSerieNr] = useState('');
  const [adresa, setAdresa] = useState('');
  const [permisNr, setPermisNr] = useState('');
  const [permisCategorie, setPermisCategorie] = useState('B');
  const [overridePhone, setOverridePhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrMsg, setOcrMsg] = useState('');
  const [result, setResult] = useState(null); // { sign_url, sms, expires_at }

  useEffect(() => {
    if (open && rental?.chirias_extra) {
      const e = rental.chirias_extra;
      setCnp(e.cnp || '');
      setCiSerieNr(e.ci_serie_nr || '');
      setAdresa(e.adresa || '');
      setPermisNr(e.permis_nr || '');
      setPermisCategorie(e.permis_categorie || 'B');
    }
    if (open) {
      setErr('');
      setOcrMsg('');
      setResult(null);
      setOverridePhone('');
    }
  }, [open, rental?.chirias_extra]);

  async function runOcr() {
    setOcrMsg('');
    setOcrBusy(true);
    try {
      const { chirias_extra } = await rentalsApi.runOcr(rental.id);
      if (chirias_extra.cnp) setCnp(chirias_extra.cnp);
      if (chirias_extra.ci_serie_nr) setCiSerieNr(chirias_extra.ci_serie_nr);
      if (chirias_extra.adresa) setAdresa(chirias_extra.adresa);
      if (chirias_extra.permis_nr) setPermisNr(chirias_extra.permis_nr);
      if (chirias_extra.permis_categorie) setPermisCategorie(chirias_extra.permis_categorie);
      const count = Object.values(chirias_extra).filter((v) => v).length;
      setOcrMsg(`Extrase ${count} câmpuri. Verifică și corectează dacă e nevoie.`);
    } catch (ex) {
      if (ex.body?.error === 'ocr_not_configured') setOcrMsg('OCR-ul nu e configurat.');
      else if (ex.body?.error === 'no_documents_uploaded') setOcrMsg('Nu există CI încărcat pe închiriere.');
      else setOcrMsg('Eroare OCR: ' + (ex.body?.error || ex.message));
    } finally {
      setOcrBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const body = {};
      if (cnp) body.cnp = cnp;
      if (ciSerieNr) body.ci_serie_nr = ciSerieNr;
      if (adresa) body.adresa = adresa;
      if (permisNr) body.permis_nr = permisNr;
      if (permisCategorie) body.permis_categorie = permisCategorie;
      if (overridePhone) body.override_phone = overridePhone;

      const res = await rentalsApi.sendForSigning(rental.id, body);
      setResult(res);
      onSigned?.();
    } catch (ex) {
      if (ex.body?.error === 'phone_required') setErr('Nu există telefon la chiriaș — completează „telefon alternativ" mai jos.');
      else if (ex.body?.error === 'already_signed') setErr('Contractul este deja semnat.');
      else setErr(ex.body?.error || 'Eroare la generare.');
    } finally {
      setBusy(false);
    }
  }

  if (!rental || !car) return null;

  // Dacă avem rezultat, afișăm doar ecranul de confirmare
  if (result) {
    return (
      <Modal open={open} onClose={onClose} title="Link de semnare generat">
        <div className="space-y-4">
          <div className="rounded-lg border border-ok/30 bg-ok/5 px-3 py-3">
            <div className="font-semibold text-ok">✓ Contract generat</div>
            <div className="mt-1 text-xs text-muted">
              {result.sms?.sent && (result.sms?.testMode
                ? `SMS TRIMIS (test mode — nu ajunge pe bune). Pentru producție: schimbă SMSLINK_TEST_MODE=false.`
                : `SMS trimis la ${rental.chirias_telefon}. Șoferul primește link-ul pe telefon.`)}
              {!result.sms?.sent && (
                `SMS nu a fost trimis (${result.sms?.reason || 'necunoscut'}). Copiază linkul manual și trimite-l șoferului.`
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Link semnare (copy → trimite)</div>
            <Copyable value={result.sign_url} as="button" className="mt-1 block w-full break-all rounded-lg border border-border bg-paper px-3 py-2 text-left font-mono text-xs text-ink hover:border-teal">
              {result.sign_url}
            </Copyable>
            <div className="mt-1 text-[11px] text-muted">
              Expiră la {new Date(result.expires_at).toLocaleString('ro-RO')}.
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={onClose} className="btn-primary">Închide</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Generează contract și trimite SMS">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg bg-cream/60 p-3 text-sm">
          <div className="font-semibold text-ink">{rental.chirias_nume}</div>
          <div className="text-muted">
            {car.marca} {car.model} · {car.numar_inmatriculare}
            {rental.chirias_telefon && <> · {rental.chirias_telefon}</>}
          </div>
          <div className="mt-1 font-mono text-xs text-muted">
            {new Date(rental.data_start).toLocaleDateString('ro-RO')} →{' '}
            {rental.data_end ? new Date(rental.data_end).toLocaleDateString('ro-RO') : 'în curs'}
            {rental.cost_contract != null && ` · ${rental.cost_contract.toLocaleString('ro-RO')} RON`}
          </div>
        </div>

        {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

        {/* Datele șoferului */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted">Date șofer (pentru contract)</div>
            {rental.id_document_path && (
              <button
                type="button" onClick={runOcr} disabled={ocrBusy}
                className="inline-flex items-center gap-1 rounded-lg border border-teal/30 bg-teal/5 px-2.5 py-1 text-[11px] font-semibold text-teal-bright hover:bg-teal/10 disabled:opacity-60"
              >
                {ocrBusy ? 'Se extrage…' : '✦ Extrage din CI'}
              </button>
            )}
          </div>
          {ocrMsg && (
            <div className="rounded-lg border border-teal/20 bg-teal/5 px-3 py-2 text-xs text-teal-bright">{ocrMsg}</div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="CNP" value={cnp} onChange={(e) => setCnp(e.target.value)}
              className="rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20" />
            <input placeholder="Seria și nr. CI" value={ciSerieNr} onChange={(e) => setCiSerieNr(e.target.value.toUpperCase())}
              className="rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20" />
          </div>
          <input placeholder="Adresă" value={adresa} onChange={(e) => setAdresa(e.target.value)}
            className="w-full rounded-lg border border-border bg-paper px-3 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20" />
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input placeholder="Număr permis" value={permisNr} onChange={(e) => setPermisNr(e.target.value.toUpperCase())}
              className="rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20" />
            <select value={permisCategorie} onChange={(e) => setPermisCategorie(e.target.value)}
              className="rounded-lg border border-border bg-paper px-3 py-2">
              {['B', 'BE', 'C', 'CE', 'D', 'DE', 'A', 'AM'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Telefon override (dacă lipsește sau vrea alt număr) */}
        {!rental.chirias_telefon && (
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Telefon șofer (obligatoriu pt SMS)</span>
            <input placeholder="07XX XXX XXX" value={overridePhone} onChange={(e) => setOverridePhone(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20" />
          </label>
        )}

        <div className="rounded-lg bg-paper p-3 text-xs text-muted">
          🖋 Semnătura ta ca proprietar e aplicată automat pe contract (din <strong>Contul meu → Semnătură</strong>).
          Clientul primește un SMS cu link pe care-l deschide pe telefonul lui — acolo semnează.
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Anulează</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Se generează…' : 'Generează și trimite SMS'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
